/**
 * Nav - Mode Card Navigation
 *
 * Card-style navigation:
 * - Cards are destinations, not tabs
 * - Icon + uppercase label
 * - Active state with gradient line at bottom
 * - Horizontal scroll on mobile, centered on desktop
 * - Home button to return to landing view
 */

import classNames from "classnames";
import { HubSection, SurfaceSectionType } from "@/hooks/hub/useGetHub";
import { FaHome } from "react-icons/fa";
import "./Nav.scss";

interface NavProps {
    sections: HubSection[];
    activeSection: string | null;
    onSectionChange: (sectionId: string) => void;
    onHomeClick: () => void;
    sectionIcons: Record<SurfaceSectionType, React.ReactNode>;
    mobileNavStyle?: "top" | "bottom";
}

export const Nav = ({
    sections,
    activeSection,
    onSectionChange,
    onHomeClick,
    sectionIcons,
    mobileNavStyle,
}: NavProps) => {
    if (sections.length === 0) {
        return null;
    }

    const isHome = activeSection === null;

    return (
        <nav className={classNames("nav", { "nav--bottom-mobile": mobileNavStyle === "bottom" })}>
            <div className="nav__cards">
                {/* Home Button - Returns to landing view */}
                <button
                    className={classNames("nav__card nav__card--home", {
                        "nav__card--active": isHome,
                    })}
                    onClick={onHomeClick}
                    type="button"
                    aria-pressed={isHome}
                >
                    <div className="nav__card-content">
                        <div className="nav__card-icon">
                            <FaHome />
                        </div>
                        <span className="nav__card-label">Home</span>
                    </div>
                    {isHome && <div className="nav__card-active-line" />}
                </button>

                {/* Section Cards */}
                {sections.map((section) =>
                    <button
                        key={section._id}
                        className={classNames("nav__card", {
                            "nav__card--active": activeSection === section._id,
                        })}
                        onClick={() => onSectionChange(section._id)}
                        type="button"
                        aria-pressed={activeSection === section._id}
                    >
                        <div className="nav__card-content">
                            {/* Icon */}
                            <div className="nav__card-icon">
                                {sectionIcons[section.type]}
                            </div>

                            {/* Label */}
                            <span className="nav__card-label">
                                {section.label}
                            </span>
                        </div>

                        {/* Active Indicator Line */}
                        {activeSection === section._id &&
                            <div className="nav__card-active-line" />
                        }
                    </button>)}
            </div>
        </nav>
    );
};
